// Singleton iframe manager — one avatar frame for the entire session.
// Lives in document.body outside React's tree; never destroyed on route change.
// Components call showFrameOver(el) to position it and hideFrame() to hide it.
// All avatar API calls go through updateAvatarContext / initAvatarSession so the
// caller doesn't need to depend on window.eventBus timing or window globals.

const AVATAR_URL_FALLBACK = import.meta.env.VITE_AVATAR_URL || "http://localhost:9031";
const AVATAR_API_URL = import.meta.env.VITE_AVATAR_API_URL || "http://localhost:9031/api";
export const isTestMode = sessionStorage.getItem('avatarTestMode') === '1';
let _apiWarned = false;
let _lastSessionContext = null; // set by initAvatarSession, reused if updateAvatarContext needs to self-heal a 404

// How to use this site — sent as part of sessionContext on every
// initAvatarSession call so it stays app-specific (the avatar itself is a
// reusable platform component embedded on multiple sites; site-usage
// instructions belong here, not baked into the avatar's own persona).
const SITE_USAGE = "How to use this website: Above the map are status filter chips — All Statuses, Design, "
  + "Construction, Completed, and Future — click one (or several) to show only projects in that phase; they "
  + "combine, so Design + Construction together shows both. A search box in the top navigation bar finds "
  + "projects and pages by name. Each project on the map has a colored marker; clicking it opens a popup with "
  + "a status badge, a short description, and buttons for Project Details (full page), Talk to an Expert "
  + "(that button is how the user opened this very chat, with that project's context already loaded here — "
  + "you are the expert it refers to, never tell the user to go click it, they're already talking to you), "
  + "and Leave Feedback. A Public Meetings card lists upcoming "
  + "public meetings with a View Calendar link to the full calendar and agendas. A Feedback form is available "
  + "site-wide for questions this chat cannot answer, including contacting city staff directly.";
let _avatarSceneUrl = null; // set from DB settings before first frame creation
let _frameLocale = "en-US"; // locale the live frame (if any) was created/last set with

// Call this when settings.avatarScene becomes available. Ideally this runs before
// getAvatarFrame() is first called, but the settings fetch is async and some callers
// (ProjectDetail, Detail, AiPanel, V2Expert) call getAvatarFrame() on their own mount,
// which can win that race — React runs child effects before parent effects, so a child
// can create the frame before App.jsx's settings-driven effect fires. If that happens
// the frame gets built on AVATAR_URL_FALLBACK; patch its src here once the real URL
// arrives instead of leaving it stuck on the fallback for the rest of the session.
export function setAvatarSceneUrl(url) {
  if (!url || url === _avatarSceneUrl) return;
  const hadRealUrl = !!_avatarSceneUrl;
  _avatarSceneUrl = url;
  if (_frame && !hadRealUrl) {
    _frame.src = `${url}?sessionId=${sessionId}&locale=${_frameLocale}${isTestMode ? '&mode=test' : ''}`;
  }
}

let _frame = null;
let _rafId = null;
let _target = null;
let _avatarStatus = 'loading'; // 'loading' | 'online' | 'offline'
const _statusListeners = new Set();

export function getAvatarStatus() { return _avatarStatus; }

export function onAvatarStatusChange(cb) {
  _statusListeners.add(cb);
  return () => _statusListeners.delete(cb);
}

function _setAvatarStatus(status) {
  _avatarStatus = status;
  _statusListeners.forEach(cb => cb(status));
}

function _getOrCreateSessionId() {
  const KEY = "tracker_avatar_sessionId";
  let id = sessionStorage.getItem(KEY);
  if (!id) {
    id = `tracker-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    sessionStorage.setItem(KEY, id);
  }
  return id;
}

// Stable session ID — same value for the lifetime of the browser session
export const sessionId = _getOrCreateSessionId();

// Returns the shared iframe, creating it on first call.
// locale is only used on first creation; subsequent callers use postMessage to switch locale.
export function getAvatarFrame(locale = "en-US") {
  if (!_frame) {
    _frameLocale = locale;
    _frame = document.createElement("iframe");
    _frame.id = "avatar-frame";
    _frame.allow = "microphone";
    _frame.title = "Avatar";
    _frame.src = `${_avatarSceneUrl || AVATAR_URL_FALLBACK}?sessionId=${sessionId}&locale=${locale}${isTestMode ? '&mode=test' : ''}`;
    Object.assign(_frame.style, {
      position: "fixed",
      border: "none",
      borderRadius: "12px",
      visibility: "hidden",
      pointerEvents: "none",
      width: "400px",
      height: "600px",
      left: "-9999px",
      top: "0",
      zIndex: "0",
    });
    _frame.addEventListener('load', () => _setAvatarStatus('online'));
    _frame.addEventListener('error', () => _setAvatarStatus('offline'));
    document.body.appendChild(_frame);
  }
  return _frame;
}

function _tick() {
  if (!_frame || !_target) return;
  if (!document.contains(_target)) {
    hideFrame();
    return;
  }
  const r = _target.getBoundingClientRect();
  if (r.width > 0 && r.height > 0) {
    Object.assign(_frame.style, {
      left: r.left + "px",
      top: r.top + "px",
      width: r.width + "px",
      height: r.height + "px",
      visibility: "visible",
      pointerEvents: "auto",
    });
  } else {
    // Target still in the DOM but collapsed to 0×0 — e.g. an ancestor like
    // .v2-aipanel going display:none below the 992px breakpoint. Without
    // this, the iframe just keeps whatever visibility/position it last had,
    // staying visible on screen indefinitely even though its target is
    // gone. Keep tracking (don't call hideFrame/stop the loop) so it
    // reappears automatically if the target becomes visible again.
    Object.assign(_frame.style, {
      visibility: "hidden",
      pointerEvents: "none",
    });
  }
  _rafId = requestAnimationFrame(_tick);
}

// Position the iframe over a DOM element, tracking it every animation frame.
export function showFrameOver(el) {
  if (!el) return;
  cancelAnimationFrame(_rafId);
  _target = el;
  _tick();
}

// Tell the avatar to stop talking/listening — same message AsideExpert sends
// on panel close. Safe to call even if the frame doesn't exist yet.
export function interruptAvatar() {
  try {
    _frame?.contentWindow?.postMessage({ type: 'INTERRUPT_AVATAR' }, '*');
  } catch (err) {
    console.debug('[Avatar] Could not send interrupt message:', err.message);
  }
}

// Hide the iframe and stop tracking.
export function hideFrame() {
  cancelAnimationFrame(_rafId);
  _target = null;
  if (!_frame) return;
  // Move off-screen but keep dimensions — resetting to 0×0 destroys the WebGL
  // framebuffer and Chrome marks the context lost after too many errors.
  Object.assign(_frame.style, {
    visibility: "hidden",
    pointerEvents: "none",
    left: "-9999px",
    top: "0",
  });
}

// POST current context to avatar server — call on every project select, page load,
// deselect, etc. Safe to call when server is down (warns once, never throws).
export async function updateAvatarContext(context) {
  if (!context || Object.keys(context).length === 0) return;
  try {
    const res = await fetch(`${AVATAR_API_URL}/update-context`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ sessionId, currentContext: context }),
    });
    if (res.status === 404) {
      // Server has no record of this sessionId — most likely it restarted
      // (contextStore is in-memory only, wiped on every restart) while this
      // tab was already open. Re-establish the session, which also carries
      // this update's currentContext through, then stop — no separate retry
      // needed since initAvatarSession's set-context call includes it.
      await initAvatarSession(_lastSessionContext, context);
      return;
    }
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
  } catch {
    if (!_apiWarned) {
      console.warn(`[Avatar] Server not responding at ${AVATAR_API_URL}. Start the avatar server to enable chat.`);
      _apiWarned = true;
    }
  }
}

// Initialize session on avatar server — call once per session (on first frame load
// or when the panel is first opened). sessionContext describes the app, currentContext
// is the initial project/page state. Safe to call when server is down.
export async function initAvatarSession(sessionContext, currentContext = {}) {
  _lastSessionContext = sessionContext;
  try {
    const res = await fetch(`${AVATAR_API_URL}/set-context`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ sessionId, sessionContext: { ...sessionContext, siteUsage: SITE_USAGE }, currentContext, mode: isTestMode ? 'test' : undefined }),
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
  } catch {
    if (!_apiWarned) {
      console.warn(`[Avatar] Server not responding at ${AVATAR_API_URL}. Start the avatar server to enable chat.`);
      _apiWarned = true;
    }
  }
}
